import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  Box, 
  CircularProgress,  
  IconButton,  
  Menu,  
  MenuItem,  
  Table, 
  TableBody, 
  TableCell, 
  TableFooter, 
  TableHead, 
  TablePagination, 
  TableRow, 
  TableSortLabel, 
  Typography 
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import useTableSortInit from '../../../../../components/hooks/useTableSortInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  GetCurrenciesBIZcore,
  SeedCurrenciesBIZcore,
  WipeCurrenciesBIZcore
} from '../../../../../redux/actions/business/core/currenciesActions';

import ConfirmationModal from '../../../../../components/modals/ConfirmationModal';
import CurrenciesModal from './CurrenciesModal';

import global from "../../../../../global.scss";

const CurrenciesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currenciesModalOpen, setCurrenciesModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [currenciesList, setCurrenciesList] = useState([]);
  const [currenciesTotal, setCurrenciesTotal] = useState(0);

  const [currenciesMenuOpen, setCurrenciesMenuOpen] = useState(false);
  const [currenciesMenuAnchor, setCurrenciesMenuAnchor] = useState(null);

  const [seedConfirmationModalOpen, setSeedConfirmationModalOpen] = useState(false);
  const [wipeConfirmationModalOpen, setWipeConfirmationModalOpen] = useState(false);

  const [
    page, 
    rowsPerPage, 
    handleChangePage, 
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const [
    sort,
    sortDirection,
    handleSort
  ] = useTableSortInit();

  // searching from a later page would land on an empty result set
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    handleChangePage(null, 0);
  };

  const tableHeadingsList = [
    {
      label: "Id",
      sort: "currency_id",
    },
    {
      label: "Name",
      sort: "name",
    },
    {
      label: "Code",
      sort: "code",
    },
    {
      label: "Symbol",
      sort: "symbol",
    },
    {
      label: "Decimals",
      sort: "decimalPosition",
    },
    {
      label: "Time stamps",
      sort: "created_at",
    },
    {
      label: "",
      sort: null
    }
  ];

  useEffect(() => {
    handleGetCurrencies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  async function handleGetCurrencies(customLoading){
    let query = ""
    if(search) query += `&search=${search}`;
    // be-core is Django/DRF — its OrderingFilter reads `ordering=field`
    if(sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    await dispatch(GetCurrenciesBIZcore(page+1, rowsPerPage, query, customLoading ? customLoading : setLoading));
  };

  async function handleSeedCurrencies(){
    setSeedConfirmationModalOpen(false);
    await dispatch(SeedCurrenciesBIZcore(setLoading));
    handleGetCurrencies();
  };

  async function handleWipeCurrencies(){
    setWipeConfirmationModalOpen(false);
    await dispatch(WipeCurrenciesBIZcore(setLoading));
    handleGetCurrencies();
  };

  useEffect(() => {
    try {
      formatCurrenciesList(state.currenciesList.records || []);
      setCurrenciesTotal(state.currenciesList.total || 0);
    } catch (err) {}
  }, [state]);

  const formatCurrenciesList = (list) => {
    setCurrenciesList(list)
  };

  return (
    <Box>

      <CurrenciesModal 
        open={currenciesModalOpen}
        setOpen={setCurrenciesModalOpen}
        title={modalTitle}
        update={update}
        page={page}
        rowsPerPage={rowsPerPage}
        />

      <ConfirmationModal
        open={seedConfirmationModalOpen}
        setOpen={setSeedConfirmationModalOpen}
        title={"Seed the standard currency list?"}
        message={"Inserts the 35 standard currencies. Rows that already exist are overwritten with the standard values; anything else in the table is left alone."}
        onConfirm={handleSeedCurrencies}
        />

      <ConfirmationModal
        open={wipeConfirmationModalOpen}
        setOpen={setWipeConfirmationModalOpen}
        title={"Wipe all currencies?"}
        message={"Permanently deletes every row in the currency table and restarts the id sequence. This cannot be undone."}
        onConfirm={handleWipeCurrencies}
        />

      <TopBar
        title={"Currencies"}
        search={search}
        searchPlaceholder={"Search id, name, code or symbol"}
        onSearchChange={handleSearchChange}
        button2Text={"Seed all currencies"}
        button2OnClick={() => {
          setSeedConfirmationModalOpen(true);
        }}
        button3Text={"Wipe all"}
        button3OnClick={() => {
          setWipeConfirmationModalOpen(true);
        }}
        button1Text={"Add Currency"}
        button1OnClick={() => {
          setUpdate({
            defaultMode: 1
          });
          setModalTitle("Add Currency");
          setCurrenciesModalOpen(!currenciesModalOpen);
        }}
        />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          currenciesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No currency found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell
                      key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell
                      key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort ? true : false}
                        direction={sortDirection}
                        onClick={() => {
                          handleSort(heading.sort);
                        }}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                currenciesList.map((row, index) => (
                  <TableRow 
                    key={index}>
                    <TableCell>
                      <Typography
                        variant='caption'
                        color={"grey"}>
                        <Copyable>
                          {row.currency_id || <NA />}
                        </Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.name || <NA />}</TableCell>
                    <TableCell>{row.code || <NA />}</TableCell>
                    <TableCell>{row.symbol || <NA />}</TableCell>
                    {/* 0 decimals is a real value (JPY, UGX, IDR) — `||` would
                        have shown N/A for it */}
                    <TableCell>{row.decimalPosition ?? <NA />}</TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                        />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setCurrenciesMenuAnchor(e.currentTarget);
                          setCurrenciesMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light']}}/>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]} 
                  colSpan={10}
                  count={currenciesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page'
                    },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                  />
              </TableRow>
            </TableFooter>
          </Table>
      }
      <Menu
        open={currenciesMenuOpen}
        anchorEl={currenciesMenuAnchor}
        onClose={() => setCurrenciesMenuOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({
              ...update,
              defaultMode: 2
            });
            setCurrenciesMenuOpen(false);
            setCurrenciesModalOpen(!currenciesModalOpen);
          }}>
          <Box
            className="flexCenter_Row"
            gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default CurrenciesPage;